import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ConsultationForm from '../components/ConsultationForm';
import '../styles/Estimator.css';

const EstimatorKitchen = () => {
  const [city, setCity] = useState('Delhi');
  const [layout, setLayout] = useState('L-Shape');
  const [length, setLength] = useState('12');
  const [finish, setFinish] = useState('Laminates');
  const [countertop, setCountertop] = useState('Granite');
  const [quoteOpen, setQuoteOpen] = useState(false);

  const estimate = useMemo(() => {
    const len = Math.max(0, Number(length) || 0);

    const finishRatePerFt = {
      Laminates: 6500,
      Acrylic: 8200,
      PU: 9500,
    }[finish] || 6500;

    const layoutMultiplier = {
      'L-Shape': 1.0,
      'U-Shape': 1.25,
      Straight: 0.9,
      Parallel: 1.15,
    }[layout] || 1.0;

    const counterAddOnPerFt = {
      Granite: 1500,
      Quartz: 2200,
      None: 0,
    }[countertop] || 0;

    const base = len * finishRatePerFt * layoutMultiplier;
    const counter = len * counterAddOnPerFt;
    const misc = base * 0.05;
    const total = Math.round(base + counter + misc);
    const low = Math.round(total * 0.92);
    const high = Math.round(total * 1.08);
    return { low, high, total, base, counter, misc };
  }, [layout, length, finish, countertop]);

  return (
    <section className="estimator-detail">
      <div className="container">
        {/* <nav aria-label="Breadcrumb" style={{marginBottom: '0.75rem'}}>
          <Link to="/estimator" className="link">← Back to Estimator</Link>
        </nav> */}
        <div className="estimator-header">
          <h1 className="section-title">Kitchen Estimator</h1>
          <p className="section-subtitle">Estimate the cost of a modular kitchen based on size, layout and finish—with light-touch Vastu and zodiac suggestions.</p>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <span className="chip" aria-label="Typical timeline">4–6 weeks</span>
            <span className="chip" aria-label="Design style">Modern</span>
          </div>
        </div>

        <div className="estimator-content">
          <div className="estimator-summary">
            <h3>What’s included</h3>
            <ul>
              <li>Base and wall units</li>
              <li>Shutters and hardware</li>
              <li>Countertop and backsplash (optional)</li>
              <li>Accessories and sink (optional)</li>
            </ul>
            <div style={{ marginTop: '1rem' }} role="status" aria-live="polite">
              <strong>Estimated range:</strong>
              <div style={{ marginTop: '0.25rem', fontSize: '1.05rem' }}>
                ₹{estimate.low.toLocaleString('en-IN')} — ₹{estimate.high.toLocaleString('en-IN')}
              </div>
              <div style={{ marginTop: '0.25rem', color: 'var(--text-secondary)' }}>Mid: ₹{estimate.total.toLocaleString('en-IN')}</div>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                <li>Cabinet base: ₹{estimate.base.toLocaleString('en-IN')}</li>
                <li>Countertop: ₹{estimate.counter.toLocaleString('en-IN')}</li>
                <li>Contingency (5%): ₹{estimate.misc.toLocaleString('en-IN')}</li>
              </ul>
              <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                Note: Prices exclude civil changes. Countertop/backsplash are optional add-ons.
              </p>
            </div>
          </div>

          <div className="estimator-form">
            <h3>Basic details</h3>
            <div className="form-row">
              <div className="form-field">
                <label>City</label>
                <select value={city} onChange={(e) => setCity(e.target.value)}>
                  <option value="Delhi">Delhi</option>
                  <option value="New Delhi">New Delhi</option>
                  <option value="Gurugram">Gurugram</option>
                  <option value="Noida">Noida</option>
                  <option value="Greater Noida">Greater Noida</option>
                  <option value="Ghaziabad">Ghaziabad</option>
                  <option value="Faridabad">Faridabad</option>
                  <option value="Dwarka">Dwarka</option>
                  <option value="Rohini">Rohini</option>
                  <option value="Saket">Saket</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Layout</label>
                <select value={layout} onChange={(e) => setLayout(e.target.value)}>
                  <option value="L-Shape">L-Shape</option>
                  <option value="U-Shape">U-Shape</option>
                  <option value="Straight">Straight</option>
                  <option value="Parallel">Parallel</option>
                </select>
              </div>
              <div className="form-field">
                <label>Length (ft)</label>
                <input
                  type="number"
                  placeholder="e.g., 12"
                  value={length}
                  min={0}
                  onChange={(e) => setLength(e.target.value)}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Finish</label>
                <select value={finish} onChange={(e) => setFinish(e.target.value)}>
                  <option value="Laminates">Laminates</option>
                  <option value="Acrylic">Acrylic</option>
                  <option value="PU">PU</option>
                </select>
              </div>
              <div className="form-field">
                <label>Countertop</label>
                <select value={countertop} onChange={(e) => setCountertop(e.target.value)}>
                  <option value="Granite">Granite</option>
                  <option value="Quartz">Quartz</option>
                  <option value="None">None</option>
                </select>
              </div>
            </div>
            <div className="estimator-actions">
              <button className="btn btn-primary" type="button">Estimate updated</button>
              <button className="btn btn-outline" type="button" onClick={() => setQuoteOpen(true)}>
                Get a detailed quote
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConsultationForm
        isOpen={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        type="quote"
        initialService="Kitchen"
      />
    </section>
  );
};

export default EstimatorKitchen;
